import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, CreditCard, LogOut, RefreshCw, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

export default function SuspendedPage() {
  const { t } = useTranslation(["auth"]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const { data: user, isLoading } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });

  const isCanceled = user?.subscriptionStatus === "canceled";
  const isPastDue = user?.subscriptionStatus === "past_due" || user?.subscriptionStatus === "suspended";

  async function openPortal() {
    setPortalLoading(true);
    setPortalError(null);
    try {
      const res = await fetch("/api/billing/portal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? t("auth:suspended.portalErrorOpen"));
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err: any) {
      setPortalError(err.message ?? t("auth:suspended.portalErrorGeneric"));
    } finally {
      setPortalLoading(false);
    }
  }

  async function signOut() {
    await apiRequest("POST", "/api/auth/logout");
    window.location.href = "/login";
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-8 h-8 rounded-full border-2 border-muted border-t-foreground animate-spin" />
      </div>
    );
  }

  if (isCanceled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-3">
              <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                <XCircle className="w-7 h-7 text-muted-foreground" />
              </div>
            </div>
            <CardTitle className="text-xl" data-testid="text-canceled-title">{t("auth:suspended.canceledTitle")}</CardTitle>
            <CardDescription className="text-sm mt-1" data-testid="text-canceled-description">
              {t("auth:suspended.canceledDesc")}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="rounded-lg bg-muted/50 border px-4 py-3 text-sm text-muted-foreground space-y-1">
              <p>{t("auth:suspended.canceledInfo")}</p>
              {user?.companyName && (
                <p className="font-medium text-foreground mt-1">{user.companyName}</p>
              )}
            </div>

            {portalError && (
              <p data-testid="text-portal-error" className="text-sm text-red-600 dark:text-red-400 text-center">
                {portalError}
              </p>
            )}

            <Button
              className="w-full gap-2"
              onClick={openPortal}
              disabled={portalLoading}
              data-testid="button-resubscribe"
            >
              {portalLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              {portalLoading ? t("auth:suspended.openingPortal") : t("auth:suspended.resubscribe")}
            </Button>

            <Button
              variant="ghost"
              className="w-full gap-2 text-muted-foreground"
              onClick={signOut}
              data-testid="button-sign-out"
            >
              <LogOut className="w-4 h-4" />
              {t("auth:suspended.signOut")}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              {t("auth:suspended.needHelp")}{" "}
              <a href="mailto:support@dealermind.ai" className="underline underline-offset-2">
                support@dealermind.ai
              </a>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-3">
            <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <CardTitle className="text-xl" data-testid="text-suspended-title">{t("auth:suspended.suspendedTitle")}</CardTitle>
          <CardDescription className="text-sm mt-1" data-testid="text-suspended-description">
            {t("auth:suspended.suspendedDesc")}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="rounded-lg bg-muted/50 border px-4 py-3 text-sm text-muted-foreground space-y-1">
            <p>{t("auth:suspended.suspendedInfo")}</p>
            {user?.companyName && (
              <p className="font-medium text-foreground mt-1">{user.companyName}</p>
            )}
          </div>

          {portalError && (
            <p data-testid="text-portal-error" className="text-sm text-red-600 dark:text-red-400 text-center">
              {portalError}
            </p>
          )}

          <Button
            className="w-full gap-2"
            onClick={openPortal}
            disabled={portalLoading}
            data-testid="button-update-payment"
          >
            {portalLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4" />
            )}
            {portalLoading ? t("auth:suspended.openingPortal") : t("auth:suspended.updatePayment")}
          </Button>

          <Button
            variant="ghost"
            className="w-full gap-2 text-muted-foreground"
            onClick={signOut}
            data-testid="button-sign-out"
          >
            <LogOut className="w-4 h-4" />
            {t("auth:suspended.signOut")}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            {t("auth:suspended.reinstate")}{" "}
            <a href="mailto:support@dealermind.ai" className="underline underline-offset-2">
              support@dealermind.ai
            </a>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
